import { fmtDateTime } from "@/lib/datetime";
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, AlertTriangle, Plug, Zap, LayoutGrid, Loader2, Activity,
  PlugZap, ScrollText, X, Settings2, MessageSquareText, Clock, Bot, Send, Search, Workflow, Circle,
  Map, Plus, Trash2, Pencil, MailCheck, Mail, ShieldCheck, ShieldAlert,
  GitBranch, Copy, Check, Tag, ArrowRight, ChevronDown, KeyRound, RotateCw, ShieldOff,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEC_IDS = ["ai", "integrations", "twin", "email", "health", "features", "automations", "ghl", "flow", "roadmap", "logs"];

const AccordionCard = ({ id, title, icon: Icon, open, onToggle, accent, note, children }) => (
  <div data-testid={`sc-card-${id}`} className={`rounded-2xl border overflow-hidden ${accent ? "border-[#F2B705]/25 bg-gradient-to-br from-[#15120a] to-[#0C0C0C]" : "border-white/10 bg-[#0C0C0C]"}`}>
    <button type="button" data-testid={`sc-toggle-${id}`} onClick={() => onToggle(id)}
      className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors">
      <span className="flex items-center gap-2.5 font-display font-bold text-white/90">
        <Icon size={18} className="text-[#F2B705]" /> {title}
        {note && <span className="text-white/30 text-xs font-normal">{note}</span>}
      </span>
      <ChevronDown size={18} className={`text-white/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="px-5 pb-5" data-testid={`sc-body-${id}`}>{children}</div>}
  </div>
);

const ago = (ts) => {
  if (!ts) return "never";
  const d = new Date(ts);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

const FEATURES = [
  { name: "Booking System", runs: "Website", services: ["GoHighLevel", "Resend"], tab: "bookings" },
  { name: "Customer CRM", runs: "Both", services: ["GoHighLevel"], tab: "customers" },
  { name: "Fleet Management", runs: "Website", services: [], tab: "fleet" },
  { name: "Rental Agreements (E-Sign)", runs: "Website", services: ["Object Storage"], tab: "agreements" },
  { name: "Loyalty Program", runs: "Website", services: [], tab: "rewards" },
  { name: "Referral Program", runs: "Website", services: [], tab: "rewards" },
  { name: "Driver Management", runs: "Website", services: ["GoHighLevel"], tab: "drivers" },
  { name: "Vehicle Inspections", runs: "Website", services: ["Object Storage"], tab: "inspections" },
  { name: "Notifications", runs: "Both", services: ["GoHighLevel", "Resend"], tab: "notifications" },
  { name: "Reviews", runs: "Website", services: [], tab: "reviews" },
  { name: "Payment Processing", runs: "Website", services: ["Stripe", "Square"], tab: "money" },
  { name: "Maintenance", runs: "Website", services: [], tab: "maintenance" },
  { name: "Recovery / Do Not Rent", runs: "Website", services: [], tab: "recovery" },
  { name: "Insurance Verification", runs: "Website", services: ["Emergent AI"], tab: "bookings" },
  { name: "Address Verification", runs: "Website", services: ["Emergent AI"], tab: "bookings" },
];

const AUTOMATIONS = [
  { name: "New Booking", trigger: "Customer submits booking", action: "Save booking, notify customer + owner, sync GHL contact", runs: "Website", ghl: true },
  { name: "Booking Confirmation SMS", trigger: "Status → Confirmed", action: "Text the customer their confirmation", runs: "GoHighLevel", ghl: true },
  { name: "Booking Confirmation Email", trigger: "Status → Confirmed", action: "Email the customer their confirmation", runs: "Website", ghl: false },
  { name: "Driver Verification", trigger: "Docs uploaded", action: "AI checks license & insurance", runs: "Website", ghl: false },
  { name: "Rental Agreement Sent", trigger: "Admin approves / re-issues", action: "Text + email a secure e-sign link", runs: "Website", ghl: true },
  { name: "Payment Reminder", trigger: "Balance owed", action: "Request the outstanding balance", runs: "Website", ghl: false, planned: true },
  { name: "Pickup Reminder", trigger: "Day before pickup", action: "Remind the customer of pickup", runs: "Website", ghl: true, planned: true },
  { name: "Return Reminder", trigger: "Day of return", action: "Remind the customer to return", runs: "Website", ghl: true, planned: true },
  { name: "Review Request", trigger: "Rental returned/closed", action: "Send a review-request link", runs: "Website", ghl: true },
  { name: "Loyalty Points Awarded", trigger: "Completed rental", action: "Accrue reward points", runs: "Website", ghl: false },
  { name: "Referral Credit Awarded", trigger: "Referral code used", action: "Apply discount + log credit", runs: "Website", ghl: false },
  { name: "Vehicle Returned", trigger: "Status → Returned", action: "Close rental, trigger review flow", runs: "Website", ghl: false },
  { name: "Customer Sync to GoHighLevel", trigger: "Any booking/lead", action: "Upsert the contact in GHL", runs: "Both", ghl: true },
];

const StatusPill = ({ status, warn }) => {
  if (warn) return <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-semibold"><AlertTriangle size={13} /> {warn}</span>;
  const map = {
    connected: <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-semibold"><CheckCircle2 size={13} /> Connected</span>,
    error: <span className="inline-flex items-center gap-1.5 text-red-400 text-xs font-semibold"><XCircle size={13} /> Error</span>,
    disconnected: <span className="inline-flex items-center gap-1.5 text-white/40 text-xs font-semibold"><XCircle size={13} /> Disconnected</span>,
  };
  return map[status] || map.disconnected;
};

const RunsBadge = ({ runs }) => {
  const map = {
    Website: "bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30",
    GoHighLevel: "bg-indigo-500/15 text-indigo-300 border-indigo-400/30",
    Both: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${map[runs] || ""}`}>{runs}</span>;
};

export default function SystemCenter({ token, onAuthError, onNavigate }) {
  const [ov, setOv] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);
  const [logs, setLogs] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [ai, setAi] = useState({ q: "", answer: "", loading: false, session: null });
  const [logRows, setLogRows] = useState(null);
  const [logCat, setLogCat] = useState("all");
  const [logQ, setLogQ] = useState("");
  const [bookings, setBookings] = useState([]);
  const [flowId, setFlowId] = useState("");
  const [flow, setFlow] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [rmForm, setRmForm] = useState({ title: "", notes: "", priority: "P2", status: "planned" });
  const [rmEditing, setRmEditing] = useState(null);
  const [rmSaving, setRmSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailHealth, setEmailHealth] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testName, setTestName] = useState("Test Customer");
  const [testVehicle, setTestVehicle] = useState("Chevrolet Malibu");
  const [sendingTest, setSendingTest] = useState(false);
  const [ghlStages, setGhlStages] = useState(null);
  const [ghlTestContact, setGhlTestContact] = useState("");
  const [copiedTag, setCopiedTag] = useState("");
  const [testingTag, setTestingTag] = useState("");
  const [runningLifecycle, setRunningLifecycle] = useState(false);
  const [ghlPipelines, setGhlPipelines] = useState(null);
  const [syncingPipelines, setSyncingPipelines] = useState(false);
  const [ghlTest, setGhlTest] = useState(null);
  const [testingConn, setTestingConn] = useState(false);
  const [pipelineCfg, setPipelineCfg] = useState({ pipeline_id: "", pipeline_name: "", stage_map: {} });
  const [savingMap, setSavingMap] = useState(false);
  const [twinStatus, setTwinStatus] = useState(null);
  const [twinBusy, setTwinBusy] = useState(false);
  const [twinReveal, setTwinReveal] = useState(null);
  const [twinCopied, setTwinCopied] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [openSecs, setOpenSecs] = useState({ ai: true });
  const toggleSec = (id) => setOpenSecs((s) => ({ ...s, [id]: !s[id] }));
  const expandAll = () => setOpenSecs(Object.fromEntries(SEC_IDS.map((i) => [i, true])));
  const collapseAll = () => setOpenSecs({});

  const load = useCallback(async () => {
    try {
      const [o, h] = await Promise.all([
        axios.get(`${API}/system/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/system/health`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setOv(o.data); setHealth(h.data);
    } catch (e) { if (e.response?.status === 401) onAuthError?.(); }
    finally { setLoading(false); }
  }, [token, onAuthError]);

  const loadEmailHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/system/email-health`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailHealth(data);
    } catch { setEmailHealth({ error: "Could not load email health." }); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/system/logs`, h).then((r) => setLogRows(r.data.logs)).catch(() => setLogRows([]));
    axios.get(`${API}/bookings`, h).then((r) => setBookings(r.data || [])).catch(() => {});
    axios.get(`${API}/system/roadmap`, h).then((r) => setRoadmap(r.data || [])).catch(() => setRoadmap([]));
    axios.get(`${API}/system/email-status`, h).then((r) => setEmailStatus(r.data)).catch(() => setEmailStatus({ error: "Could not load email status." }));
    loadEmailHealth();
    axios.get(`${API}/system/ghl-stages`, h).then((r) => setGhlStages(r.data)).catch(() => setGhlStages({ items: [], total: 0, configured: 0 }));
    axios.get(`${API}/system/ghl/pipeline-config`, h).then((r) => { if (r.data?.pipeline_id) setPipelineCfg({ pipeline_id: r.data.pipeline_id, pipeline_name: r.data.pipeline_name || "", stage_map: r.data.stage_map || {} }); }).catch(() => {});
    loadTwinStatus();
  }, [token, loadEmailHealth]);

  const loadTwinStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/twin-key/status`, { headers: { Authorization: `Bearer ${token}` } });
      setTwinStatus(data);
    } catch { setTwinStatus({ active: false }); }
  }, [token]);

  const generateOrRotateTwinKey = async () => {
    if (twinStatus?.active && !window.confirm("This will immediately invalidate the current Twin key. Continue?")) return;
    setTwinBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/twin-key/generate`, {}, auth);
      setTwinReveal(data.key);
      setTwinCopied(false);
      await loadTwinStatus();
      toast.success("New Twin key generated. Copy it now — it won't be shown again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the Twin key.");
    } finally { setTwinBusy(false); }
  };

  const revokeTwinKey = async () => {
    if (!window.confirm("Revoke the active Twin key? Twin will immediately lose read access.")) return;
    setTwinBusy(true);
    try {
      await axios.post(`${API}/admin/twin-key/revoke`, {}, auth);
      await loadTwinStatus();
      toast.success("Twin key revoked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke the Twin key.");
    } finally { setTwinBusy(false); }
  };

  const copyTwinKey = () => {
    if (!twinReveal) return;
    navigator.clipboard?.writeText(twinReveal);
    setTwinCopied(true);
    setTimeout(() => setTwinCopied(false), 2000);
  };

  const copyTag = (tag) => {
    navigator.clipboard?.writeText(tag);
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(""), 1500);
  };

  const toggleStageConfigured = async (tag, configured) => {
    setGhlStages((s) => ({ ...s, items: s.items.map((i) => i.tag === tag ? { ...i, configured } : i), configured: s.items.filter((i) => i.tag === tag ? configured : i.configured).length }));
    try {
      await axios.patch(`${API}/system/ghl-stages/${tag}`, { configured }, auth);
      const { data } = await axios.get(`${API}/system/ghl-stages`, auth);
      setGhlStages(data);
    } catch { toast.error("Could not update status."); }
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      const { data } = await axios.get(`${API}/system/ghl/test-connection`, auth);
      setGhlTest(data);
      if (data.ok) toast.success("GoHighLevel connection is healthy — all permissions OK.");
      else toast.error(data.summary || "Some GHL permissions are missing.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not test the connection."); }
    finally { setTestingConn(false); }
  };

  const syncPipelines = async () => {
    setSyncingPipelines(true);
    try {
      const { data } = await axios.get(`${API}/system/ghl/pipelines`, auth);
      setGhlPipelines(data.pipelines || []);
      if ((data.pipelines || []).length === 0) {
        toast.error("No pipelines found in your GoHighLevel location.");
      } else {
        // Auto-select the AutoHire Rental Pipeline if present and nothing chosen yet.
        const auto = data.pipelines.find((p) => /autohire|rental/i.test(p.name || ""));
        if (auto && !pipelineCfg.pipeline_id) setPipelineCfg((c) => ({ ...c, pipeline_id: auto.id, pipeline_name: auto.name }));
        toast.success(`Fetched ${data.pipelines.length} pipeline(s) from GoHighLevel.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch pipelines from GoHighLevel.");
    } finally { setSyncingPipelines(false); }
  };

  const selectPipeline = (id) => {
    const p = (ghlPipelines || []).find((x) => x.id === id);
    setPipelineCfg((c) => ({ ...c, pipeline_id: id, pipeline_name: p?.name || "", stage_map: {} }));
  };

  const mapStage = (tag, stageId) => {
    setPipelineCfg((c) => ({ ...c, stage_map: { ...c.stage_map, [tag]: stageId } }));
  };

  const saveMapping = async () => {
    if (!pipelineCfg.pipeline_id) { toast.error("Select a pipeline first."); return; }
    setSavingMap(true);
    try {
      await axios.post(`${API}/system/ghl/pipeline-config`, pipelineCfg, auth);
      const { data } = await axios.get(`${API}/system/ghl-stages`, auth);
      setGhlStages(data);
      toast.success("Pipeline mapping saved. New bookings will create & move opportunities automatically.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save pipeline mapping.");
    } finally { setSavingMap(false); }
  };

  const selectedPipeline = (ghlPipelines || []).find((p) => p.id === pipelineCfg.pipeline_id);
  const mappedCount = ghlStages?.items?.filter((i) => pipelineCfg.stage_map?.[i.tag]).length || 0;


  const testTag = async (tag) => {
    setTestingTag(tag);
    try {
      const { data } = await axios.post(`${API}/system/ghl/test-tag`, { tag, email: ghlTestContact.trim() || undefined }, auth);
      toast.success(`Tag "${tag}" applied to ${data.tagged} in GoHighLevel. Check your pipeline moved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test tag.");
    } finally { setTestingTag(""); }
  };

  const runFullLifecycle = async () => {
    setRunningLifecycle(true);
    try {
      const { data } = await axios.post(`${API}/system/ghl/test-lifecycle`, { email: ghlTestContact.trim() || undefined }, auth);
      toast.success(`Firing all ${data.tags.length} stage tags to ${data.tagged} over ~${data.eta_seconds}s. Watch your GHL pipeline move through every stage.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the lifecycle test.");
    } finally { setTimeout(() => setRunningLifecycle(false), 3000); }
  };

  const sendTestEmail = async () => {
    const to = testTo.trim();
    if (!/^[\w.\-+]+@[\w.\-]+\.\w+$/.test(to)) { toast.error("Enter a valid email address."); return; }
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/system/test-customer-email`, { to, name: testName, vehicle: testVehicle }, auth);
      toast.success(`Test email sent to ${data.sent_to}. ${data.sandbox ? "⚠️ Sandbox mode — only reaches your Resend account." : "Check the inbox (and spam)."}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test email.");
    } finally { setSendingTest(false); }
  };

  const resetRm = () => { setRmForm({ title: "", notes: "", priority: "P2", status: "planned" }); setRmEditing(null); };

  const saveRoadmap = async () => {
    if (!rmForm.title.trim()) { toast.error("Add a title first."); return; }
    setRmSaving(true);
    try {
      if (rmEditing) {
        await axios.patch(`${API}/system/roadmap/${rmEditing}`, rmForm, auth);
        toast.success("Roadmap item updated.");
      } else {
        await axios.post(`${API}/system/roadmap`, rmForm, auth);
        toast.success("Added to roadmap.");
      }
      const { data } = await axios.get(`${API}/system/roadmap`, auth);
      setRoadmap(data || []);
      resetRm();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save roadmap item."); }
    finally { setRmSaving(false); }
  };

  const editRoadmap = (item) => {
    setRmEditing(item.id);
    setRmForm({ title: item.title, notes: item.notes || "", priority: item.priority, status: item.status });
  };

  const deleteRoadmap = async (id) => {
    try {
      await axios.delete(`${API}/system/roadmap/${id}`, auth);
      setRoadmap((r) => (r || []).filter((x) => x.id !== id));
      if (rmEditing === id) resetRm();
      toast.success("Removed from roadmap.");
    } catch { toast.error("Could not delete item."); }
  };

  const askAi = async (question) => {
    const q = (question ?? ai.q).trim();
    if (!q) return;
    setAi((s) => ({ ...s, q, loading: true, answer: "" }));
    try {
      const { data } = await axios.post(`${API}/system/ask`, { question: q, session_id: ai.session }, auth);
      setAi((s) => ({ ...s, answer: data.answer, session: data.session_id, loading: false }));
    } catch (e) {
      setAi((s) => ({ ...s, loading: false }));
      toast.error(e?.response?.data?.detail || "AI request failed.");
    }
  };

  const loadFlow = async (id) => {
    setFlowId(id); setFlow(null);
    if (!id) return;
    try { const { data } = await axios.get(`${API}/system/flow/${id}`, auth); setFlow(data); }
    catch { toast.error("Could not load flow."); }
  };

  const testConn = async (key) => {
    setTesting(key);
    try {
      const { data } = await axios.post(`${API}/system/test/${key}`, {}, auth);
      toast.success(`Test sent to ${data.sent_to}. Check it arrived!`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection test failed.");
    } finally { setTesting(null); }
  };

  const viewLogs = async () => {
    setLogsOpen(true); setLogs(null);
    try {
      const { data } = await axios.get(`${API}/notifications/recent?limit=40`, auth);
      setLogs(data);
    } catch { setLogs([]); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={20} /> Loading System Center…</div>;

  const integrations = ov?.integrations || [];

  return (
    <div className="space-y-4" data-testid="system-center">
      <div>
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><Plug size={22} className="text-[#F2B705]" /> System Center</h2>
        <p className="text-white/50 text-sm mt-1">Integrations, automations, and live system health — all in one place.</p>
        {ov?.notifications_disabled && (
          <div className="mt-3 text-xs text-amber-400 flex items-center gap-1.5 border border-amber-400/30 bg-amber-400/[0.06] rounded px-3 py-2">
            <AlertTriangle size={14} /> Notifications are DISABLED on this environment — no emails/texts are being sent.
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <button data-testid="sc-expand-all" onClick={expandAll}
          className="text-xs font-semibold border border-white/15 text-white/60 px-3 py-1.5 rounded-lg hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors">Expand All</button>
        <button data-testid="sc-collapse-all" onClick={collapseAll}
          className="text-xs font-semibold border border-white/15 text-white/60 px-3 py-1.5 rounded-lg hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors">Collapse All</button>
      </div>

      {/* AI Assistant */}
      <AccordionCard id="ai" title="AI Assistant" note="· GPT-5.5" icon={Bot} accent open={openSecs.ai} onToggle={toggleSec}>
        <p className="text-white/45 text-sm mt-1">Ask anything about your live operations — rentals due, overdue payments, revenue, maintenance.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {["Which rentals are due today?", "Who has overdue payments?", "Which vehicles generate the most revenue?", "Which vehicles need maintenance?", "Recommend operational improvements."].map((s) => (
            <button key={s} data-testid={`ai-suggestion`} onClick={() => askAi(s)} disabled={ai.loading}
              className="text-xs border border-white/15 text-white/65 px-3 py-1.5 rounded-full hover:border-[#F2B705]/50 hover:text-[#F2B705] transition-colors disabled:opacity-50">{s}</button>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <input data-testid="ai-input" value={ai.q} onChange={(e) => setAi((s) => ({ ...s, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && askAi()} placeholder="Ask the assistant…"
            className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
          <button data-testid="ai-send" onClick={() => askAi()} disabled={ai.loading}
            className="bg-[#F2B705] text-black font-bold px-5 rounded-lg hover:bg-white transition-colors flex items-center gap-2 disabled:opacity-60">
            {ai.loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
          </button>
        </div>
        {ai.answer && (
          <div data-testid="ai-answer" className="mt-4 bg-[#0A0A0A] border border-white/10 rounded-lg p-4 text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{ai.answer}</div>
        )}
      </AccordionCard>

      {/* Live Integrations */}
      <AccordionCard id="integrations" title="Live Integrations" icon={PlugZap} open={openSecs.integrations} onToggle={toggleSec}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {integrations.map((it) => (
            <div key={it.key} data-testid={`sc-integration-${it.key}`} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-white/85 text-sm font-semibold">{it.label}</span>
                {it.planned ? <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/40">Planned</span> : <StatusPill status={it.status} warn={it.warn} />}
              </div>
              {it.detail && <p className="text-white/40 text-xs mt-1.5">{it.detail}</p>}
              {!it.planned && (
                <div className="flex items-center gap-4 mt-2 text-[11px] text-white/40">
                  <span className="flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-400/70" /> Sync: {ago(it.last_success)}</span>
                  {it.last_failure && <span className="flex items-center gap-1 text-red-400/80"><XCircle size={11} /> Fail: {ago(it.last_failure)}</span>}
                </div>
              )}
              {!it.planned && (
                <div className="flex items-center gap-2 mt-3">
                  {it.testable && (
                    <button data-testid={`sc-test-${it.key}`} onClick={() => testConn(it.key)} disabled={testing === it.key}
                      className="text-xs flex items-center gap-1.5 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-60">
                      {testing === it.key ? <Loader2 className="animate-spin" size={12} /> : <Zap size={12} />} Test Connection
                    </button>
                  )}
                  {(it.key === "gohighlevel" || it.key === "resend") && (
                    <button data-testid={`sc-logs-${it.key}`} onClick={viewLogs} className="text-xs flex items-center gap-1.5 border border-white/15 text-white/60 px-3 py-1.5 rounded hover:bg-white/10 transition-colors">
                      <ScrollText size={12} /> View Logs
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </AccordionCard>

      {/* Twin Read-Only Access */}
      <AccordionCard id="twin" title="Twin Read-Only Access" icon={KeyRound} open={openSecs.twin} onToggle={toggleSec}>
        <p className="text-white/45 text-sm mt-1 mb-3">
          A dedicated, revocable API key for the Twin MCP connector — scoped to <code className="text-[#F2B705]/90 bg-[#F2B705]/10 px-1.5 py-0.5 rounded text-xs">ops:read</code> only,
          limited to the two read-only tools (operations snapshot + booking lookup). Separate from the ChatGPT OAuth connector.
        </p>
        <div className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5" data-testid="twin-key-card">
          {twinStatus === null ? (
            <div className="flex items-center gap-2 text-white/50 py-3"><Loader2 className="animate-spin" size={16} /> Checking Twin key status…</div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2.5">
                  {twinStatus.active ? (
                    <span data-testid="twin-key-status-active" className="inline-flex items-center gap-1.5 text-emerald-400 text-sm font-semibold border border-emerald-400/30 bg-emerald-400/[0.06] px-3 py-1 rounded-full">
                      <CheckCircle2 size={14} /> Active
                    </span>
                  ) : (
                    <span data-testid="twin-key-status-revoked" className="inline-flex items-center gap-1.5 text-white/40 text-sm font-semibold border border-white/15 px-3 py-1 rounded-full">
                      <XCircle size={14} /> No active key
                    </span>
                  )}
                  {twinStatus.last_four && (
                    <code className="text-white/50 text-xs font-mono bg-white/[0.04] px-2 py-1 rounded">twin_…{twinStatus.last_four}</code>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="twin-key-generate-btn" onClick={generateOrRotateTwinKey} disabled={twinBusy}
                    className="flex items-center gap-2 bg-[#F2B705] text-black font-bold text-xs px-3.5 py-2 rounded-lg hover:bg-white transition-colors disabled:opacity-60">
                    {twinBusy ? <Loader2 className="animate-spin" size={14} /> : twinStatus.active ? <RotateCw size={14} /> : <KeyRound size={14} />}
                    {twinStatus.active ? "Rotate Key" : "Generate Key"}
                  </button>
                  {twinStatus.active && (
                    <button data-testid="twin-key-revoke-btn" onClick={revokeTwinKey} disabled={twinBusy}
                      className="flex items-center gap-2 border border-red-400/40 text-red-400 text-xs font-bold px-3.5 py-2 rounded-lg hover:bg-red-400 hover:text-black transition-colors disabled:opacity-60">
                      <ShieldOff size={14} /> Revoke
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 text-[11px]" data-testid="twin-key-meta">
                <div className="bg-black/40 border border-white/10 rounded-md py-2 px-2.5">
                  <div className="text-white/40 uppercase tracking-wide">Created</div>
                  <div className="text-white/75 mt-0.5">{twinStatus.created_at ? ago(twinStatus.created_at * 1000) : "—"}</div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-md py-2 px-2.5">
                  <div className="text-white/40 uppercase tracking-wide">Last Used</div>
                  <div className="text-white/75 mt-0.5">{twinStatus.last_used_at ? ago(twinStatus.last_used_at * 1000) : "never"}</div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded-md py-2 px-2.5">
                  <div className="text-white/40 uppercase tracking-wide">Scope</div>
                  <div className="text-white/75 mt-0.5">ops:read</div>
                </div>
              </div>
              {!twinStatus.active && (
                <p className="text-white/30 text-xs mt-3">Generate a key, then paste it into Twin's connector setup as a standard <code className="text-white/50">Authorization: Bearer</code> header. It is shown only once.</p>
              )}
            </>
          )}
        </div>
      </AccordionCard>

      {/* Email Delivery */}
      <AccordionCard id="email" title="Email Delivery" icon={MailCheck} open={openSecs.email} onToggle={toggleSec}>
        {emailHealth && !emailHealth.error && (
          <div className="mb-3 bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="email-health-panel">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-white font-bold text-sm">📬 Email Health</span>
              {emailHealth.daily_pct > 100 && <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 text-[10px] font-semibold">Over daily quota — new emails queue until midnight UTC</span>}
              {emailHealth.notifications_disabled && <span className="px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 text-[10px] font-semibold">Test mode — sends disabled in this environment</span>}
              <button data-testid="email-health-refresh" onClick={loadEmailHealth} className="ml-auto text-white/40 hover:text-white text-xs">↻ Refresh</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-center">
              {[["Sent Today", `${emailHealth.sent_today}/${emailHealth.daily_limit}`, emailHealth.daily_pct >= 90 ? "text-red-400" : emailHealth.daily_pct >= 70 ? "text-[#F2B705]" : "text-green-400"],
                ["Daily Quota", `${emailHealth.daily_pct}%`, emailHealth.daily_pct >= 90 ? "text-red-400" : "text-white"],
                ["This Month", `${emailHealth.sent_month}/${emailHealth.monthly_limit}`, emailHealth.monthly_pct >= 90 ? "text-red-400" : "text-white"],
                ["Failed Today", emailHealth.failed_today, emailHealth.failed_today ? "text-red-400" : "text-white/60"],
                ["Queued", emailHealth.queued_count, emailHealth.queued_count ? "text-[#F2B705]" : "text-white/60"],
                ["Duplicates Blocked", emailHealth.duplicates_prevented_total, "text-blue-300"]].map(([l, v, c]) => (
                <div key={l} className="bg-black/40 border border-white/10 rounded-md py-2 px-1">
                  <div className={`font-display font-black text-lg ${c}`}>{v}</div>
                  <div className="text-white/40 text-[10px]">{l}</div>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-2 text-[11px]">
              {emailHealth.last_sent && <div className="text-white/45">Last sent: <span className="text-white/70">{emailHealth.last_sent.subject?.slice(0, 50)}</span> → {emailHealth.last_sent.to?.slice(0, 35)} <span className="text-white/30">({fmtDateTime(emailHealth.last_sent.at)})</span></div>}
              {emailHealth.last_failed && <div className="text-red-300/70">Last failed: {emailHealth.last_failed.subject?.slice(0, 40)} — {emailHealth.last_failed.error?.slice(0, 60)}</div>}
            </div>
            {(emailHealth.queued.length > 0 || emailHealth.failed_queue.length > 0) && (
              <div className="mt-3 space-y-1.5" data-testid="email-queue-list">
                <div className="text-white/40 text-[10px] uppercase tracking-wider">Queued & Failed Emails</div>
                {[...emailHealth.queued.map((q) => ({ ...q, st: "queued" })), ...emailHealth.failed_queue.map((q) => ({ ...q, st: "failed" }))].map((q) => (
                  <div key={q.id} className="flex flex-wrap items-center gap-2 bg-black/40 border border-white/10 rounded-md px-2.5 py-1.5 text-xs">
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${q.st === "queued" ? "bg-[#F2B705]/15 text-[#F2B705]" : "bg-red-500/15 text-red-300"}`}>{q.st === "queued" ? "⏳ QUEUED" : "✗ FAILED"}</span>
                    <span className="text-white/75 truncate max-w-[220px]">{q.subject}</span>
                    <span className="text-white/40 truncate max-w-[160px]">→ {q.to}</span>
                    {q.st === "queued" && q.next_retry_at && <span className="text-white/30">retry {fmtDateTime(q.next_retry_at)}</span>}
                    <span className="text-white/25">×{q.attempts}</span>
                    <div className="ml-auto flex gap-1">
                      <button data-testid={`email-retry-${q.id}`} onClick={async () => { try { const { data } = await axios.post(`${API}/system/email-queue/${q.id}/retry`, {}, auth); toast[data.result === "sent" ? "success" : "error"](data.result === "sent" ? "Email sent!" : `Still ${data.result}: ${data.error || "quota"}`); loadEmailHealth(); } catch { toast.error("Retry failed."); } }}
                        className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white text-[10px]">Retry now</button>
                      <button data-testid={`email-discard-${q.id}`} onClick={async () => { try { await axios.delete(`${API}/system/email-queue/${q.id}`, auth); loadEmailHealth(); } catch { toast.error("Could not discard."); } }}
                        className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-white/40 hover:text-red-300 text-[10px]">Discard</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Status card */}
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="email-status-card">
            {emailStatus === null ? (
              <div className="flex items-center gap-2 text-white/50 py-3"><Loader2 className="animate-spin" size={16} /> Checking email config…</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white/45 text-[11px] uppercase tracking-wide">Sending From</div>
                    <div className="text-white/90 text-sm font-semibold mt-0.5 truncate" data-testid="email-sender">{emailStatus.sender || "—"}</div>
                  </div>
                  <Mail size={18} className="text-[#F2B705] shrink-0" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {emailStatus.domain_verified === true && (
                    <span data-testid="email-domain-verified" className="inline-flex items-center gap-1.5 text-xs text-emerald-400 border border-emerald-400/30 bg-emerald-400/[0.06] px-2.5 py-1 rounded-full">
                      <ShieldCheck size={13} /> {emailStatus.domain} verified
                    </span>
                  )}
                  {emailStatus.domain_verified === false && (
                    <span data-testid="email-domain-unverified" className="inline-flex items-center gap-1.5 text-xs text-red-400 border border-red-400/30 bg-red-400/[0.06] px-2.5 py-1 rounded-full">
                      <ShieldAlert size={13} /> {emailStatus.domain} {emailStatus.domain_status === "not_found" ? "not added to Resend" : `(${emailStatus.domain_status || "unverified"})`}
                    </span>
                  )}
                  {emailStatus.sandbox && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 border border-amber-400/30 bg-amber-400/[0.06] px-2.5 py-1 rounded-full">
                      <AlertTriangle size={13} /> Sandbox — only reaches your Resend account
                    </span>
                  )}
                  {!emailStatus.api_key_set && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-red-400 border border-red-400/30 bg-red-400/[0.06] px-2.5 py-1 rounded-full">
                      <XCircle size={13} /> RESEND_API_KEY not set
                    </span>
                  )}
                </div>
                {emailStatus.error && <p className="text-amber-400/80 text-xs">{emailStatus.error}</p>}
                {emailStatus.domain_verified === false && !emailStatus.error && (
                  <p className="text-white/45 text-xs leading-relaxed">Add &amp; verify <b className="text-white/70">{emailStatus.domain}</b> in your <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#F2B705] hover:underline">Resend dashboard</a> (add the DNS records they give you). Until then, customer emails may not deliver.</p>
                )}
              </div>
            )}
          </div>

          {/* Test sender card */}
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="email-test-card">
            <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-1"><Send size={15} className="text-[#F2B705]" /> Send Test Customer Email</h4>
            <p className="text-white/40 text-xs mb-3">Sends a real booking-confirmation email to any address. No booking is created.</p>
            <div className="space-y-2">
              <input data-testid="test-email-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                placeholder="recipient@example.com"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="test-email-name" value={testName} onChange={(e) => setTestName(e.target.value)}
                  placeholder="Customer name"
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
                <input data-testid="test-email-vehicle" value={testVehicle} onChange={(e) => setTestVehicle(e.target.value)}
                  placeholder="Vehicle"
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
              </div>
              <button data-testid="send-test-email" onClick={sendTestEmail} disabled={sendingTest}
                className="w-full bg-[#F2B705] text-black font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {sendingTest ? <Loader2 className="animate-spin" size={15} /> : <MailCheck size={15} />}
                {sendingTest ? "Sending…" : "Send Test Email"}
              </button>
            </div>
          </div>
        </div>
      </AccordionCard>

      {/* System Health */}
      <AccordionCard id="health" title="System Health" icon={Activity} open={openSecs.health} onToggle={toggleSec}>
        {health?.failed_today > 0 && (
          <div className="mb-3 text-xs text-red-400 flex items-center gap-1.5 border border-red-400/30 bg-red-400/[0.06] rounded px-3 py-2">
            <AlertTriangle size={13} /> {health.failed_today} failed send(s) today{health.last_failure?.error ? ` — last error: ${health.last_failure.error}` : ""}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(health?.items || []).map((i) => (
            <div key={i.label} data-testid={`sc-health-${i.label}`} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-white/45 text-[11px] uppercase tracking-wide">{i.label}</span>
                {i.ok ? <CheckCircle2 size={13} className="text-emerald-400" /> : <Clock size={13} className="text-white/30" />}
              </div>
              <div className="text-white/80 text-sm font-semibold mt-1.5 flex items-center gap-1"><Clock size={11} className="text-white/30" /> {ago(i.ts)}</div>
              {i.detail && <div className="text-white/35 text-[11px] mt-0.5 truncate">{i.detail}</div>}
              {i.error && <div className="text-red-400/80 text-[11px] mt-0.5 truncate">{i.error}</div>}
            </div>
          ))}
        </div>
      </AccordionCard>

      {/* Website Feature Map */}
      <AccordionCard id="features" title="Website Feature Map" icon={LayoutGrid} open={openSecs.features} onToggle={toggleSec}>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="bg-white/[0.03] text-white/45 text-left text-xs uppercase tracking-wider">
              <th className="px-4 py-3 font-semibold">Feature</th><th className="px-4 py-3 font-semibold">Runs In</th>
              <th className="px-4 py-3 font-semibold">Connected Services</th><th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Configure</th></tr></thead>
            <tbody>
              {FEATURES.map((f) => (
                <tr key={f.name} data-testid={`sc-feature-${f.name}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white/90 font-medium">{f.name}</td>
                  <td className="px-4 py-3"><RunsBadge runs={f.runs} /></td>
                  <td className="px-4 py-3 text-white/50">{f.services.length ? f.services.join(", ") : <span className="text-white/25">—</span>}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs"><CheckCircle2 size={13} /> Active</span></td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`sc-configure-${f.name}`} onClick={() => onNavigate?.(f.tab)} className="text-xs inline-flex items-center gap-1.5 border border-white/15 text-white/70 px-3 py-1.5 rounded hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors">
                      <Settings2 size={12} /> Configure
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AccordionCard>

      {/* Automation Control Center */}
      <AccordionCard id="automations" title="Automation Control Center" icon={Zap} open={openSecs.automations} onToggle={toggleSec}>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm min-w-[820px]">
            <thead><tr className="bg-white/[0.03] text-white/45 text-left text-xs uppercase tracking-wider">
              <th className="px-4 py-3 font-semibold">Automation</th><th className="px-4 py-3 font-semibold">Trigger</th>
              <th className="px-4 py-3 font-semibold">Action</th><th className="px-4 py-3 font-semibold">Runs In</th>
              <th className="px-4 py-3 font-semibold text-center">GHL</th><th className="px-4 py-3 font-semibold text-center">Status</th></tr></thead>
            <tbody>
              {AUTOMATIONS.map((a) => (
                <tr key={a.name} data-testid={`sc-automation-${a.name}`} className="border-t border-white/5 align-top hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white/90 font-medium">{a.name}</td>
                  <td className="px-4 py-3 text-white/50">{a.trigger}</td>
                  <td className="px-4 py-3 text-white/50">{a.action}</td>
                  <td className="px-4 py-3"><RunsBadge runs={a.runs} /></td>
                  <td className="px-4 py-3 text-center">{a.ghl ? <MessageSquareText size={14} className="inline text-emerald-400" /> : <span className="text-white/25">—</span>}</td>
                  <td className="px-4 py-3 text-center">
                    {a.planned
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full border border-white/15 text-white/40">Planned</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-400/30 text-emerald-400 bg-emerald-400/10">Active</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-white/30 text-xs mt-2">Enable/Disable toggles &amp; per-automation logs arrive in a later phase. "Active" reflects automations wired and live today.</p>
      </AccordionCard>

      {/* GoHighLevel CRM Sync & Pipeline Mapping */}
      <AccordionCard id="ghl" title="GoHighLevel CRM Sync & Pipeline Mapping" icon={GitBranch} open={openSecs.ghl} onToggle={toggleSec}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2"><GitBranch size={14} /> GoHighLevel Pipeline Sync</h3>
          {ghlStages && (
            <span data-testid="ghl-progress-count" className="text-xs text-white/50">{ghlStages.configured}/{ghlStages.total} workflows configured</span>
          )}
        </div>

        {/* Opportunity Pipeline Mapping — creates & moves a real GHL Opportunity per booking */}
        <div className="bg-[#0C0C0C] border border-[#F2B705]/20 rounded-2xl p-5 mb-3" data-testid="ghl-opportunity-mapping">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <h4 className="text-sm text-white font-semibold flex items-center gap-2"><Workflow size={15} className="text-[#F2B705]" /> Opportunity Pipeline Mapping</h4>
              <p className="text-white/45 text-xs mt-1 leading-relaxed max-w-2xl">
                Map each rental stage to a stage in your GoHighLevel pipeline. AutoHire will then create one <b className="text-white/70">Opportunity</b> per booking and move it through the pipeline automatically — its value tracks the booking total.
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              <button data-testid="ghl-test-connection" onClick={testConnection} disabled={testingConn}
                className="flex items-center gap-2 border border-emerald-400/40 text-emerald-400 text-xs font-bold px-3.5 py-2 rounded-lg hover:bg-emerald-400 hover:text-black transition-colors disabled:opacity-60">
                {testingConn ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />} Test Connection
              </button>
              <button data-testid="ghl-sync-pipelines" onClick={syncPipelines} disabled={syncingPipelines}
                className="flex items-center gap-2 border border-[#F2B705]/40 text-[#F2B705] text-xs font-bold px-3.5 py-2 rounded-lg hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-60">
                {syncingPipelines ? <Loader2 className="animate-spin" size={14} /> : <PlugZap size={14} />} Sync Pipelines from GHL
              </button>
            </div>
          </div>

          {ghlTest && (
            <div className="mt-4" data-testid="ghl-test-results">
              <div className={`flex items-center gap-2 text-sm font-semibold mb-1 ${ghlTest.ok ? "text-emerald-400" : "text-red-400"}`} data-testid="ghl-test-summary">
                {ghlTest.ok ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />} {ghlTest.summary}
              </div>
              {ghlTest.token_hint && (
                <div className="text-xs text-white/45 mb-2" data-testid="ghl-token-identity">
                  Token in use: <span className="font-mono text-white/70">{ghlTest.token_hint}</span> · {ghlTest.token_type} · Location <span className="font-mono text-white/70">{ghlTest.location_id}</span>
                  <span className="text-white/30"> — compare the ending against your token in GHL → Settings → Private Integrations.</span>
                </div>
              )}
              {ghlTest.configured && (
                <div className="divide-y divide-white/5 border border-white/10 rounded-lg overflow-hidden">
                  {ghlTest.checks.map((ch) => (
                    <div key={ch.name} className="px-3 py-2.5 bg-[#0A0A0A]" data-testid={`ghl-check-${ch.scope}`}>
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        {ch.ok ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0" /> : <XCircle size={14} className="text-red-400 shrink-0" />}
                        <span className="text-white/85 font-semibold">{ch.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/45 font-mono">{ch.scope}</span>
                        {ch.status && !ch.ok ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-bold">HTTP {ch.status}</span> : null}
                        <span className="ml-auto text-xs text-white/35">{ch.why}</span>
                      </div>
                      {!ch.ok && (
                        <div className="mt-1.5 ml-6 text-xs text-red-300/80 leading-relaxed">
                          {ch.detail ? <div className="text-white/40 mb-0.5">GHL said: “{ch.detail}”</div> : null}
                          <span className="text-[#F2B705]/90">Fix:</span> {ch.fix}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {pipelineCfg.pipeline_id && (
            <div className="mt-3 text-xs text-emerald-400 flex items-center gap-1.5" data-testid="ghl-active-pipeline">
              <CheckCircle2 size={13} /> Active pipeline: <b className="text-white/80">{pipelineCfg.pipeline_name || pipelineCfg.pipeline_id}</b> · {mappedCount}/{ghlStages?.total || 0} stages mapped
            </div>
          )}

          {ghlPipelines !== null && (
            <div className="mt-4">
              <label className="text-white/50 text-xs font-semibold block mb-1.5">Pipeline</label>
              <select data-testid="ghl-pipeline-select" value={pipelineCfg.pipeline_id} onChange={(e) => selectPipeline(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F2B705]/50">
                <option value="">Select a pipeline…</option>
                {ghlPipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>

              {selectedPipeline && (
                <>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="ghl-stage-map-grid">
                    {(ghlStages?.items || []).map((s) => (
                      <div key={s.tag} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-2.5">
                        <div className="text-white/80 text-xs font-semibold mb-1.5">{s.stage}</div>
                        <select data-testid={`ghl-map-${s.tag}`} value={pipelineCfg.stage_map?.[s.tag] || ""} onChange={(e) => mapStage(s.tag, e.target.value)}
                          className="w-full bg-[#0C0C0C] border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#F2B705]/50">
                          <option value="">— not mapped —</option>
                          {selectedPipeline.stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <button data-testid="ghl-save-mapping" onClick={saveMapping} disabled={savingMap}
                    className="mt-4 w-full flex items-center justify-center gap-2 bg-[#F2B705] text-black font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors disabled:opacity-60">
                    {savingMap ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Save Pipeline Mapping
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {ghlStages === null ? (
          <div className="flex items-center gap-2 text-white/50 py-6 justify-center"><Loader2 className="animate-spin" size={16} /> Loading pipeline stages…</div>
        ) : (
          <>
            {/* Setup checklist progress */}
            <div className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5 mb-3" data-testid="ghl-checklist">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/80 font-semibold">Setup Checklist</span>
                <span className="text-xs text-[#F2B705] font-bold">{Math.round((ghlStages.configured / ghlStages.total) * 100)}% complete</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-2 bg-[#F2B705] rounded-full transition-all" style={{ width: `${(ghlStages.configured / ghlStages.total) * 100}%` }} />
              </div>
              <p className="text-white/40 text-xs mt-3 leading-relaxed">
                In GoHighLevel, create one Workflow per tag below: <b className="text-white/70">Trigger</b> = "Contact Tag Added" (the exact tag) → <b className="text-white/70">Action</b> = "Update Opportunity → move to the matching stage". Then mark it Configured here.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <input data-testid="ghl-test-contact" value={ghlTestContact} onChange={(e) => setGhlTestContact(e.target.value)}
                  placeholder="Test contact email (defaults to owner email)"
                  className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
                <span className="text-white/30 text-xs hidden sm:block">used by "Test Tag"</span>
              </div>
              <button data-testid="ghl-test-lifecycle" onClick={runFullLifecycle} disabled={runningLifecycle}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-[#F2B705] text-black font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors disabled:opacity-60">
                {runningLifecycle ? <Loader2 className="animate-spin" size={15} /> : <GitBranch size={15} />}
                Test Full Lifecycle — fire all 11 tags in order
              </button>
              <p className="text-white/35 text-[11px] mt-1.5">Sends every stage tag ~2s apart to your test contact so one opportunity walks the whole pipeline. Use a throwaway test contact.</p>
            </div>

            {/* Stage rows */}
            <div className="space-y-2" data-testid="ghl-stage-list">
              {ghlStages.items.map((s) => (
                <div key={s.tag} data-testid={`ghl-stage-${s.tag}`}
                  className={`bg-[#0C0C0C] border rounded-xl p-4 ${s.configured ? "border-emerald-400/25" : "border-white/10"}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-[#F2B705] text-sm font-mono bg-[#F2B705]/10 px-2 py-0.5 rounded">{s.tag}</code>
                        <ArrowRight size={13} className="text-white/30" />
                        <span className="text-white/85 text-sm font-semibold">{s.stage}</span>
                      </div>
                      <p className="text-white/45 text-xs mt-1.5">{s.desc}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`ghl-copy-${s.tag}`} onClick={() => copyTag(s.tag)} title="Copy tag"
                        className="text-xs flex items-center gap-1.5 border border-white/15 text-white/60 px-2.5 py-1.5 rounded hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors">
                        {copiedTag === s.tag ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />} {copiedTag === s.tag ? "Copied" : "Copy"}
                      </button>
                      <button data-testid={`ghl-test-${s.tag}`} onClick={() => testTag(s.tag)} disabled={testingTag === s.tag}
                        className="text-xs flex items-center gap-1.5 border border-white/15 text-white/60 px-2.5 py-1.5 rounded hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors disabled:opacity-50">
                        {testingTag === s.tag ? <Loader2 className="animate-spin" size={12} /> : <Tag size={12} />} Test Tag
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${s.configured ? "text-emerald-400" : "text-white/40"}`}>
                      {s.configured ? <CheckCircle2 size={13} /> : <Circle size={13} />} {s.configured ? "Configured" : "Not Configured"}
                    </span>
                    <button data-testid={`ghl-toggle-${s.tag}`} onClick={() => toggleStageConfigured(s.tag, !s.configured)}
                      className={`text-xs px-3 py-1.5 rounded border transition-colors ${s.configured ? "border-white/15 text-white/50 hover:border-white/30" : "border-[#F2B705]/40 text-[#F2B705] hover:bg-[#F2B705] hover:text-black"}`}>
                      {s.configured ? "Mark Not Configured" : "Mark Configured"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </AccordionCard>

      {/* Data Flow Visualizer */}
      <AccordionCard id="flow" title="Data Flow Visualizer" icon={Workflow} open={openSecs.flow} onToggle={toggleSec}>
        <select data-testid="flow-select" value={flowId} onChange={(e) => loadFlow(e.target.value)}
          className="bg-[#0C0C0C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-4 w-full sm:w-auto">
          <option value="">Select a booking to trace its data flow…</option>
          {bookings.filter((b) => b.status !== "cancelled").map((b) => (
            <option key={b.id} value={b.id}>{b.name} — {b.vehicle} ({(b.pickup_date || "").slice(0, 10)})</option>
          ))}
        </select>
        {flow && (
          <div className="flex flex-wrap items-stretch gap-2" data-testid="flow-steps">
            {flow.steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`rounded-lg border px-3 py-2 min-w-[130px] ${s.status === "done" ? "border-emerald-400/30 bg-emerald-400/[0.06]" : "border-white/10 bg-[#0C0C0C]"}`}>
                  <div className="flex items-center gap-1.5 text-sm">
                    {s.status === "done" ? <CheckCircle2 size={14} className="text-emerald-400" /> : <Circle size={14} className="text-white/30" />}
                    <span className={s.status === "done" ? "text-white/90" : "text-white/50"}>{s.label}</span>
                  </div>
                  {s.detail && <div className="text-white/35 text-[11px] mt-0.5 ml-5 truncate max-w-[140px]">{s.detail}</div>}
                </div>
                {i < flow.steps.length - 1 && <span className="text-white/20">→</span>}
              </div>
            ))}
          </div>
        )}
      </AccordionCard>

      {/* Roadmap / Future Features */}
      <AccordionCard id="roadmap" title="Roadmap / Future Features" icon={Map} open={openSecs.roadmap} onToggle={toggleSec}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Add / Edit form */}
          <div className="bg-[#0C0C0C] border border-white/10 rounded-2xl p-5">
            <h4 className="font-display font-bold text-sm flex items-center gap-2 mb-3">
              {rmEditing ? <><Pencil size={15} className="text-[#F2B705]" /> Edit Item</> : <><Plus size={15} className="text-[#F2B705]" /> Add Roadmap Item</>}
            </h4>
            <input data-testid="rm-title" value={rmForm.title} onChange={(e) => setRmForm((s) => ({ ...s, title: e.target.value }))}
              placeholder="Feature title (e.g. Automation toggles)"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50" />
            <textarea data-testid="rm-notes" value={rmForm.notes} onChange={(e) => setRmForm((s) => ({ ...s, notes: e.target.value }))}
              placeholder="Notes / details (optional)" rows={3}
              className="w-full mt-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/50 resize-none" />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <select data-testid="rm-priority" value={rmForm.priority} onChange={(e) => setRmForm((s) => ({ ...s, priority: e.target.value }))}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F2B705]/50">
                <option value="P0">P0 — Critical</option>
                <option value="P1">P1 — High</option>
                <option value="P2">P2 — Nice to have</option>
              </select>
              <select data-testid="rm-status" value={rmForm.status} onChange={(e) => setRmForm((s) => ({ ...s, status: e.target.value }))}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F2B705]/50">
                <option value="planned">Planned</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid="rm-save" onClick={saveRoadmap} disabled={rmSaving}
                className="flex-1 bg-[#F2B705] text-black font-bold text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {rmSaving ? <Loader2 className="animate-spin" size={15} /> : rmEditing ? <Pencil size={15} /> : <Plus size={15} />}
                {rmEditing ? "Update" : "Add"}
              </button>
              {rmEditing && (
                <button data-testid="rm-cancel" onClick={resetRm} className="text-sm border border-white/15 text-white/60 px-4 py-2 rounded-lg hover:border-white/30 transition-colors">Cancel</button>
              )}
            </div>
          </div>

          {/* Roadmap list */}
          <div className="lg:col-span-2 space-y-2" data-testid="rm-list">
            {roadmap === null && <div className="flex items-center gap-2 text-white/50 py-6 justify-center"><Loader2 className="animate-spin" size={16} /> Loading roadmap…</div>}
            {roadmap && roadmap.length === 0 && (
              <div className="bg-[#0C0C0C] border border-dashed border-white/15 rounded-2xl p-8 text-center text-white/40 text-sm">
                No roadmap items yet. Add your first planned feature on the left.
              </div>
            )}
            {roadmap && roadmap.map((item) => {
              const pri = { P0: "bg-red-500/15 text-red-400 border-red-400/30", P1: "bg-amber-500/15 text-amber-400 border-amber-400/30", P2: "bg-white/[0.06] text-white/50 border-white/15" }[item.priority] || "bg-white/[0.06] text-white/50 border-white/15";
              const st = { planned: "text-white/50", in_progress: "text-[#F2B705]", done: "text-emerald-400" }[item.status] || "text-white/50";
              const stLabel = { planned: "Planned", in_progress: "In Progress", done: "Done" }[item.status] || item.status;
              return (
                <div key={item.id} data-testid={`rm-item-${item.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${pri}`}>{item.priority}</span>
                      <span className={`text-xs font-semibold ${st}`}>{stLabel}</span>
                    </div>
                    <div className={`text-sm font-medium mt-1.5 ${item.status === "done" ? "text-white/40 line-through" : "text-white/90"}`}>{item.title}</div>
                    {item.notes && <p className="text-white/45 text-xs mt-1 leading-relaxed">{item.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`rm-edit-${item.id}`} onClick={() => editRoadmap(item)} className="p-1.5 text-white/40 hover:text-[#F2B705] transition-colors"><Pencil size={15} /></button>
                    <button data-testid={`rm-delete-${item.id}`} onClick={() => deleteRoadmap(item.id)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </AccordionCard>

      {/* System Logs */}
      <AccordionCard id="logs" title="System Logs" icon={ScrollText} open={openSecs.logs} onToggle={toggleSec}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-2"><ScrollText size={14} /> System Logs</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input data-testid="logs-search" value={logQ} onChange={(e) => setLogQ(e.target.value)} placeholder="Search…"
                className="bg-[#0C0C0C] border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F2B705]/40 w-40" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {["all", "bookings", "payments", "messages", "agreements", "reviews"].map((c) => (
            <button key={c} data-testid={`logs-filter-${c}`} onClick={() => setLogCat(c)}
              className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${logCat === c ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:border-white/30"}`}>{c}</button>
          ))}
        </div>
        <div className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-[420px] overflow-y-auto" data-testid="logs-table">
          {logRows === null && <div className="flex items-center gap-2 text-white/50 py-6 justify-center"><Loader2 className="animate-spin" size={16} /> Loading logs…</div>}
          {logRows && logRows
            .filter((l) => logCat === "all" || l.category === logCat)
            .filter((l) => !logQ || `${l.type} ${l.detail}`.toLowerCase().includes(logQ.toLowerCase()))
            .slice(0, 100)
            .map((l, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-white/[0.02]">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-white/[0.06] text-white/50 shrink-0">{l.category}</span>
                  <span className="text-white/80 truncate">{l.type}</span>
                  <span className="text-white/40 truncate hidden sm:inline">· {l.detail}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  {l.status === "failed" ? <span className="text-red-400 text-xs">failed</span> : l.status === "pending" ? <span className="text-amber-400 text-xs">pending</span> : <span className="text-emerald-400/80 text-xs">ok</span>}
                  <span className="text-white/30 text-xs">{ago(l.ts)}</span>
                </span>
              </div>
            ))}
          {logRows && logRows.length === 0 && <p className="text-white/40 text-sm text-center py-6">No events logged yet.</p>}
        </div>
      </AccordionCard>

      {logsOpen && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={() => setLogsOpen(false)} data-testid="sc-logs-modal">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h4 className="font-display font-bold flex items-center gap-2"><ScrollText size={18} className="text-[#F2B705]" /> Notification Logs</h4>
              <button onClick={() => setLogsOpen(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto p-4 space-y-1.5">
              {logs === null && <div className="flex items-center gap-2 text-white/50 py-6 justify-center"><Loader2 className="animate-spin" size={16} /> Loading…</div>}
              {logs && logs.length === 0 && <p className="text-white/40 text-sm text-center py-6">No notifications logged yet.</p>}
              {logs && logs.map((l, i) => (
                <div key={i} className="flex items-center justify-between gap-3 bg-[#0C0C0C] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${l.channel === "email" ? "bg-sky-500/15 text-sky-300" : "bg-indigo-500/15 text-indigo-300"}`}>{l.channel}</span>
                    <span className="text-white/70 truncate">{l.type || "—"}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    {l.status === "sent" ? <span className="text-emerald-400 text-xs">sent</span> : l.status === "failed" ? <span className="text-red-400 text-xs">failed</span> : <span className="text-white/40 text-xs">{l.status}</span>}
                    <span className="text-white/30 text-xs">{ago(l.ts)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {twinReveal && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="twin-key-reveal-modal">
          <div className="bg-[#0A0A0A] border border-[#F2B705]/30 rounded-xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h4 className="font-display font-bold flex items-center gap-2"><KeyRound size={18} className="text-[#F2B705]" /> Twin API Key — Copy It Now</h4>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-amber-400 text-sm flex items-center gap-1.5"><AlertTriangle size={14} /> This key will not be shown again. Store it in Twin's connector setup now.</p>
              <div className="flex items-center gap-2">
                <code data-testid="twin-key-reveal-value" className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F2B705] font-mono break-all">{twinReveal}</code>
                <button data-testid="twin-key-copy-btn" onClick={copyTwinKey}
                  className="shrink-0 flex items-center gap-1.5 border border-white/15 text-white/70 px-3 py-2.5 rounded-lg hover:border-[#F2B705]/40 hover:text-[#F2B705] transition-colors text-xs font-semibold">
                  {twinCopied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />} {twinCopied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-white/40 text-xs">Send it as a standard <code className="text-white/60">Authorization: Bearer &lt;key&gt;</code> header. Scope: ops:read. Tools: get_operations_snapshot, get_booking.</p>
              <button data-testid="twin-key-reveal-close" onClick={() => setTwinReveal(null)}
                className="w-full bg-[#F2B705] text-black font-bold text-sm px-4 py-2.5 rounded-lg hover:bg-white transition-colors mt-2">
                I've Copied It — Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
